import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Lock, FileCheck, Download, CheckCircle, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';

// Import compliance logos
import gdprLogo from "@/assets/gdpr.png";
import euaiactLogo from "@/assets/euaiact.png";
import hippaLogo from "@/assets/hippa.png";
import doraLogo from "@/assets/dora.png";
import isoLogo from "@/assets/isologo.png";
import pciDssLogo from "@/assets/pcidss.png";
import soc2Type1Logo from "@/assets/Soc2Type1certified.png";
import soc2Type2Logo from "@/assets/Soc2type2.png";

const Compliance = () => {
  const { t } = useTranslation();
  
  const certifications = [
    {
      name: t('compliance.gdpr_name'),
      fullName: t('compliance.gdpr_full'),
      abbr: "GDPR",
      logoUrl: gdprLogo,
      status: t('compliance.gdpr_status'),
      description: t('compliance.gdpr_desc'),
      highlights: [t('compliance.gdpr_feature_1'), t('compliance.gdpr_feature_2'), t('compliance.gdpr_feature_3'), t('compliance.gdpr_feature_4')],
      colors: "from-blue-500 to-cyan-500"
    },
    {
      name: t('compliance.euai_name'),
      fullName: t('compliance.euai_full'),
      abbr: "EU AI",
      logoUrl: euaiactLogo,
      status: t('compliance.euai_status'),
      description: t('compliance.euai_desc'),
      highlights: [t('compliance.euai_feature_1'), t('compliance.euai_feature_2'), t('compliance.euai_feature_3'), t('compliance.euai_feature_4')],
      colors: "from-purple-500 to-blue-500"
    },
    {
      name: t('compliance.hipaa_name'),
      fullName: t('compliance.hipaa_full'),
      abbr: "HIPAA",
      logoUrl: hippaLogo,
      status: t('compliance.hipaa_status'),
      description: t('compliance.hipaa_desc'),
      highlights: [t('compliance.hipaa_feature_1'), t('compliance.hipaa_feature_2'), t('compliance.hipaa_feature_3'), t('compliance.hipaa_feature_4')],
      colors: "from-green-500 to-teal-500"
    },
    {
      name: t('compliance.dora_name'),
      fullName: t('compliance.dora_full'),
      abbr: "DORA",
      logoUrl: doraLogo,
      status: t('compliance.dora_status'),
      description: t('compliance.dora_desc'),
      highlights: [t('compliance.dora_feature_1'), t('compliance.dora_feature_2'), t('compliance.dora_feature_3'), t('compliance.dora_feature_4')],
      colors: "from-amber-500 to-orange-500"
    },
    {
      name: t('compliance.soc2_type1_name'),
      fullName: t('compliance.soc2_type1_full'),
      abbr: "SOC2 I",
      logoUrl: soc2Type1Logo,
      status: t('compliance.soc2_type1_status'),
      description: t('compliance.soc2_type1_desc'),
      highlights: [t('compliance.soc2_type1_feature_1'), t('compliance.soc2_type1_feature_2'), t('compliance.soc2_type1_feature_3'), t('compliance.soc2_type1_feature_4')],
      colors: "from-indigo-500 to-purple-500"
    },
    {
      name: t('compliance.soc2_type2_name'),
      fullName: t('compliance.soc2_type2_full'),
      abbr: "SOC2 II",
      logoUrl: soc2Type2Logo,
      status: t('compliance.soc2_type2_status'),
      description: t('compliance.soc2_type2_desc'),
      highlights: [t('compliance.soc2_type2_feature_1'), t('compliance.soc2_type2_feature_2'), t('compliance.soc2_type2_feature_3'), t('compliance.soc2_type2_feature_4')],
      colors: "from-violet-500 to-purple-600"
    },
    {
      name: t('compliance.iso_name'),
      fullName: t('compliance.iso_full'),
      abbr: "ISO 27001",
      logoUrl: isoLogo,
      status: t('compliance.iso_status'),
      description: t('compliance.iso_desc'),
      highlights: [t('compliance.iso_feature_1'), t('compliance.iso_feature_2'), t('compliance.iso_feature_3'), t('compliance.iso_feature_4')],
      colors: "from-blue-600 to-indigo-600"
    },
    {
      name: t('compliance.pci_name'),
      fullName: t('compliance.pci_full'),
      abbr: "PCI DSS",
      logoUrl: pciDssLogo,
      status: t('compliance.pci_status'),
      description: t('compliance.pci_desc'),
      highlights: [t('compliance.pci_feature_1'), t('compliance.pci_feature_2'), t('compliance.pci_feature_3'), t('compliance.pci_feature_4')],
      colors: "from-red-500 to-pink-600"
    },
  ];

  const timeline = [
    { year: "2025", event: t('compliance.timeline_2025_iso') },
    { year: "2025", event: t('compliance.timeline_2025_gdpr') },
    { year: "2025", event: t('compliance.timeline_2025_soc2') },
    { year: "2025", event: t('compliance.timeline_2025_pci') },
    { year: "2025", event: t('compliance.timeline_2025_hipaa') },
    { year: "2025", event: t('compliance.timeline_2025_euai') },
    { year: "2025", event: t('compliance.timeline_2025_dora') },
  ];

  const securityMetrics = [
    { metric: "0", label: t('compliance.metric_breaches'), color: "text-green-500" },
    { metric: "100%", label: t('compliance.metric_uptime'), color: "text-blue-500" },
    { metric: "256-bit", label: t('compliance.metric_encryption'), color: "text-purple-500" },
    { metric: "24/7", label: t('compliance.metric_monitoring'), color: "text-cyan-500" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />

      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 rounded-full glass-effect border border-primary/30">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-semibold">{t('compliance.hero_badge')}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight">
              {t('compliance.hero_title')} <span className="gradient-brand-text">{t('compliance.hero_title_highlight')}</span>
            </h1>
            <p className="text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              {t('compliance.hero_subtitle')}
            </p>
          </motion.div>

          {/* Security Metrics */}
          <div className="grid md:grid-cols-4 gap-8 mb-24">
            {securityMetrics.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="h-full"
              >
                <Card className="p-12 glass-effect border-primary/40 text-center hover-lift group h-full flex flex-col items-center justify-center">
                  <div className={`text-7xl font-bold mb-4 ${item.color} group-hover:scale-110 transition-transform`}>{item.metric}</div>
                  <div className="text-lg text-muted-foreground font-bold text-balance">{item.label}</div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Certifications Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-20 text-center tracking-tight">
              {t('compliance.certifications_title')} <span className="gradient-brand-text">{t('compliance.certifications_highlight')}</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-12 glass-effect border-primary/40 hover:shadow-2xl hover-lift transition-all duration-500 h-full group">
                    <div className="text-center mb-8">
                      <div className="mb-6 group-hover:scale-105 transition-transform duration-500">
                        <div className="flex items-center justify-center h-28">
                          <img
                            src={cert.logoUrl}
                            alt={`${cert.name} logo`}
                            className="max-h-24 max-w-[200px] w-auto h-auto object-contain"
                          />
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold mb-3 tracking-tight">{cert.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{cert.fullName}</p>
                      <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-500/20 border-2 border-green-500/40 text-green-400 text-sm font-bold">
                        <CheckCircle className="w-5 h-5" />
                        {cert.status}
                      </span>
                    </div>

                    <p className="text-muted-foreground mb-8 leading-relaxed text-base text-center">{cert.description}</p>

                    <div className="pt-6 border-t border-border">
                      <h4 className="font-bold text-sm mb-4 text-center">{t('compliance.key_features')}</h4>
                      <ul className="space-y-3">
                        {cert.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm">
                            <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Compliance Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight">
              {t('compliance.timeline_title')} <span className="gradient-brand-text">{t('compliance.timeline_highlight')}</span>
            </h2>
            <Card className="p-8 md:p-12 glass-effect border-primary/20">
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-6 pb-6 border-b border-border/30 last:border-0"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{item.year}</span>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <p className="text-lg font-semibold">{item.event}</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Security Architecture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight">
              {t('compliance.security_title')} <span className="gradient-brand-text">{t('compliance.security_highlight')}</span>
            </h2>
            <Card className="p-10 glass-effect border-primary/20">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold mb-3">{t('compliance.security_encryption_title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('compliance.security_encryption_desc')}</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold mb-3">{t('compliance.security_access_title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('compliance.security_access_desc')}</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4">
                    <FileCheck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold mb-3">{t('compliance.security_monitoring_title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('compliance.security_monitoring_desc')}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Download Center */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight">
              {t('compliance.docs_title')} <span className="gradient-brand-text">{t('compliance.docs_highlight')}</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: t('compliance.doc_whitepaper_title'), desc: t('compliance.doc_whitepaper_desc'), icon: FileCheck },
                { title: t('compliance.doc_soc2_title'), desc: t('compliance.doc_soc2_desc'), icon: Award },
                { title: t('compliance.doc_gdpr_title'), desc: t('compliance.doc_gdpr_desc'), icon: Shield },
                { title: t('compliance.doc_pentest_title'), desc: t('compliance.doc_pentest_desc'), icon: Lock },
              ].map((doc, index) => (
                <Card key={index} className="p-6 glass-effect border-primary/20 hover:shadow-xl transition-all cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center group-hover:scale-110 transition-transform">
                      <doc.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-bold mb-1">{doc.title}</h3>
                      <p className="text-sm text-muted-foreground">{doc.desc}</p>
                    </div>
                    <Download className="w-5 h-5 text-primary group-hover:translate-y-1 transition-transform" />
                  </div>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="p-16 glass-effect border-primary/30 bg-gradient-to-br from-primary/20 to-accent/20 text-center hover-lift">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">{t('compliance.cta_title')}</h2>
              <p className="text-xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed">
                {t('compliance.cta_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="gradient-brand text-white hover:opacity-90">
                    {t('compliance.cta_contact')}
                  </Button>
                </Link>
                <Link to="/platform">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    {t('compliance.cta_platform')}
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Compliance;